import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { scheduleApi } from "../api/schedule";

export function useSchedule(start?: string, end?: string, includeInactive?: boolean) {
  return useQuery({
    queryKey: ["schedule", start, end, includeInactive ?? false],
    queryFn: () => scheduleApi.getSchedule(start, end, includeInactive),
    staleTime: 0,
  });
}

export function useScheduleEntries(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["schedule-entries", params],
    queryFn: () => scheduleApi.listEntries(params),
    staleTime: 0,
  });
}

export function useCreateScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { entry_type: string; start_date: string; end_date: string; note?: string }) =>
      scheduleApi.createEntry(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["schedule-entries"] });
    },
  });
}

export function useDeleteScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduleApi.deleteEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule"] }),
  });
}

export function useApproveScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      scheduleApi.approveEntry(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["schedule-entries"] });
    },
  });
}

export function useRejectScheduleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      scheduleApi.rejectEntry(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["schedule-entries"] });
    },
  });
}

export function useSetRemoteHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { start_hour?: string | null; hours_ranges?: Array<{ start: string; end: string }> | null } }) =>
      scheduleApi.setEntryHours(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule"] }),
  });
}

export function useCompanyHolidays() {
  return useQuery({
    queryKey: ["company-holidays"],
    queryFn: () => scheduleApi.listCompanyHolidays(),
  });
}

export function useCreateCompanyHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { start_date: string; end_date: string; description?: string; compensates_for?: string | null }) =>
      scheduleApi.createCompanyHoliday(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["company-holidays"] });
      qc.invalidateQueries({ queryKey: ["employee-days-off"] });
    },
  });
}

export function useDeleteCompanyHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduleApi.deleteCompanyHoliday(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["company-holidays"] });
      qc.invalidateQueries({ queryKey: ["employee-days-off"] });
    },
  });
}

/** Individual days off of one employee (+ their first working day for the "already given" check). */
export function useEmployeeDaysOff(employeeId: string | null | undefined) {
  return useQuery({
    queryKey: ["employee-days-off", "list", employeeId],
    queryFn: () => scheduleApi.listEmployeeDaysOff(employeeId as string),
    enabled: !!employeeId,
  });
}

/** Employees still owed a day off in lieu of a holiday — all in scope, or one. */
export function useOwedDaysOff(employeeId?: string, enabled = true) {
  return useQuery({
    queryKey: ["employee-days-off", "owed", employeeId ?? "all"],
    queryFn: () => scheduleApi.listOwedDaysOff(employeeId),
    enabled,
  });
}

export function useCreateEmployeeDayOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { employee: string; date: string; compensates_for: string; description?: string }) =>
      scheduleApi.createEmployeeDayOff(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-days-off"] });
      qc.invalidateQueries({ queryKey: ["schedule"] });
    },
  });
}

export function useDeleteEmployeeDayOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduleApi.deleteEmployeeDayOff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-days-off"] });
      qc.invalidateQueries({ queryKey: ["schedule"] });
    },
  });
}
